import { motion } from 'framer-motion';
import { Shield, Clock, Users, Award, Heart, TrendingUp, CheckCircle } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Your safety is our top priority with verified drivers and well-maintained vehicles.'
    },
    {
      icon: Clock,
      title: '24/7 Service',
      description: 'Round-the-clock availability for all your travel needs, anytime, anywhere.'
    },
    {
      icon: Users,
      title: 'Professional Team',
      description: 'Experienced and courteous drivers committed to excellent service.'
    },
    {
      icon: Award,
      title: 'Top Rated',
      description: 'Consistently rated as one of the best cab services in Dehradun.'
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Happy Customers' },
    { value: '50,000+', label: 'Trips Completed' },
    { value: '15+', label: 'Years Experience' },
    { value: '4.9/5', label: 'Customer Rating' }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="about-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              About <span className="text-blue-600">Explore Destination Cab</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Your trusted travel partner in Dehradun, committed to providing safe, comfortable, and affordable cab services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1703693620961-863d96247208?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkZWhyYWR1biUyMGxhbmRzY2FwZSUyMG1vdW50YWluc3xlbnwwfHx8fDE3NjkzMzQyNDN8MA&ixlib=rb-4.1.0&q=85"
                alt="Dehradun Beautiful Landscape"
                className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <p className="text-sm text-orange-500 font-semibold uppercase tracking-wider">Our Story</p>
              <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight leading-tight">
                Serving Dehradun Since Years
              </h2>
              
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  <strong className="text-slate-900">Explore Destination Cab</strong> is a trusted cab service based in Dehradun, offering safe, comfortable, and affordable taxi services across India. We specialize in airport transfers, outstation trips, and Uttarakhand tour packages.
                </p>
                
                <p>
                  With years of experience in the transportation industry, we have built a reputation for reliability, professionalism, and customer satisfaction. Our team of experienced drivers and well-maintained fleet ensure that every journey with us is smooth and memorable.
                </p>
                
                <p>
                  Whether you're a tourist exploring the beautiful landscapes of Uttarakhand, a local resident needing daily transportation, or a business traveler requiring airport transfers, we provide customized solutions to meet your specific travel needs.
                </p>
              </div>

              <div className="pt-6">
                <a
                  href="tel:+916396867682"
                  data-testid="about-cta-btn"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5"
                >
                  Contact Us Today
                </a>
              </div>
            </motion.div>
          </div>

          {/* Values Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-24"
          >
            <div className="text-center mb-16">
              <p className="text-sm text-orange-500 font-semibold uppercase tracking-wider mb-4">Our Values</p>
              <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight">
                What We Stand For
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`value-card-${index}`}
                  className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-300 group gradient-card-hover text-center"
                >
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl inline-block mb-6 group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-12 md:p-16 text-white"
            data-testid="stats-section"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">Our Journey in Numbers</h2>
              <p className="text-blue-100 text-lg">Building trust through consistent service excellence</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`stat-${index}`}
                  className="text-center"
                >
                  <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                  <div className="text-blue-100 text-sm md:text-base">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Why Choose Us */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-24"
          >
            <div className="text-center mb-16">
              <p className="text-sm text-orange-500 font-semibold uppercase tracking-wider mb-4">Why Choose Us</p>
              <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight">
                Your Reliable Travel Partner
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                'Well-maintained and sanitized vehicles',
                'Experienced and verified drivers',
                'Transparent pricing with no hidden costs',
                '24/7 customer support and service',
                'Flexible booking and cancellation policy',
                'Coverage across Uttarakhand and beyond',
                'Professional and courteous service',
                'On-time pickup and drop services'
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="flex items-start gap-4 bg-slate-50 p-6 rounded-xl hover:bg-blue-50 transition-colors duration-300"
                >
                  <div className="bg-green-100 p-1.5 rounded-full mt-0.5">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-slate-700 font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Mission Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-24 bg-gradient-to-br from-orange-50 to-white p-12 md:p-16 rounded-3xl border border-orange-100"
          >
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center bg-orange-100 p-4 rounded-2xl mb-6">
                <Heart className="w-10 h-10 text-orange-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Our Mission</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                To provide safe, reliable, and affordable transportation services that exceed customer expectations. 
                We are committed to making every journey comfortable, memorable, and hassle-free while maintaining 
                the highest standards of professionalism and customer care.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
